import type { ReactNode } from 'react';
import type { ExcerptLine } from '@/domain/transcript/excerpts';
import { KEY_POINT_LABEL, type KeyPointKind } from '@/domain/transcript/keyPoints';

const mmss = (ms: number) => `${Math.floor(ms / 60000)}:${String(Math.floor((ms % 60000) / 1000)).padStart(2, '0')}`;
const ROLE: Record<string, string> = { counselor: '상', client: '내', unknown: '?', mixed: '?' };
const KINDS = Object.keys(KEY_POINT_LABEL) as KeyPointKind[];

/** 포인트 꼬리표 */
export function PointChips({ points, compact }: { points: Partial<Record<KeyPointKind, number>> | KeyPointKind[]; compact?: boolean }) {
  const list: [KeyPointKind, number | null][] = Array.isArray(points)
    ? points.map((k) => [k, null])
    : KINDS.filter((k) => points[k]).map((k) => [k, points[k]!]);
  if (!list.length) return null;
  return (
    <span className={`point-chips${compact ? ' compact' : ''}`}>
      {list.map(([k, n]) => (
        <span key={k} className={`point-chip pk-${k}`}>
          {KEY_POINT_LABEL[k]}
          {n !== null ? ` ${n}` : ''}
        </span>
      ))}
    </span>
  );
}

/**
 * 구간 카드 (A32): 회기·시간, 회기 전체에서의 위치(막대), 핵심 포인트, 이유, 펼치면 줄마다 포인트 표시.
 * 포인트 표시는 고를 때만 보이고 문서에는 들어가지 않는다.
 */
export function RangeCard({
  title,
  badges,
  startMs,
  endMs,
  sessionEndMs,
  points,
  reason,
  lines,
  chosen,
  actions,
}: {
  title: string;
  badges?: ReactNode;
  startMs: number;
  endMs: number;
  sessionEndMs: number;
  points: Partial<Record<KeyPointKind, number>>;
  reason?: string | undefined;
  lines: ExcerptLine[];
  chosen?: boolean | undefined;
  actions: ReactNode;
}) {
  const total = Math.max(sessionEndMs, endMs, 1);
  const left = (startMs / total) * 100;
  const width = Math.max(2, ((endMs - startMs) / total) * 100);
  const minutes = Math.max(1, Math.round((endMs - startMs) / 60000));
  return (
    <article className={`range-card${chosen ? ' chosen' : ''}`}>
      <header className="range-head">
        <strong>{title}</strong>
        <span className="muted small">
          {mmss(startMs)} ~ {mmss(endMs)} · 약 {minutes}분
        </span>
        {badges}
      </header>
      <div className="range-bar" aria-label={`회기 전체 중 ${mmss(startMs)}부터 ${mmss(endMs)}까지`}>
        <span style={{ left: `${left}%`, width: `${width}%` }} />
      </div>
      <PointChips points={points} />
      {reason && <p className="small range-reason">{reason.replace(/^핵심 포인트: [^,]*,?\s*/, '')}</p>}
      <details>
        <summary className="small">대화 보기 (포인트 표시)</summary>
        <ol className="range-lines">
          {lines.map((l) => (
            <li key={l.segmentId} className={l.points?.length ? 'pointed' : undefined}>
              <span className="who">{ROLE[l.role] ?? '?'}</span>
              <span className="what">
                {l.text}
                {l.points?.length ? <PointChips points={l.points} compact /> : null}
              </span>
            </li>
          ))}
        </ol>
      </details>
      <div className="row">{actions}</div>
    </article>
  );
}
